import type { ReactNode } from "react";

type ButtonVariant = "primary" | "accent" | "ghost" | "outline" | "onDark" | "outlineLight";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps {
  children: ReactNode;
  href?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  iconRight?: string;
  external?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-primary-500 text-background-50 hover:bg-primary-600",
  accent: "bg-accent-500 text-foreground-950 hover:bg-accent-600",
  ghost:
    "border border-background-300 bg-background-50 text-foreground-900 hover:border-primary-400 hover:text-primary-700",
  outline: "border border-primary-500 text-primary-700 hover:bg-primary-500 hover:text-background-50",
  onDark: "bg-background-50 text-foreground-950 hover:bg-background-100",
  outlineLight: "border border-background-50/40 text-background-50 hover:bg-background-50/10",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3.5 py-2 text-sm",
  md: "px-5 py-2.5 text-sm",
  lg: "px-6 py-3.5 text-[15px]",
};

export default function Button({
  children,
  href,
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  external = false,
  type = "button",
  onClick,
  className = "",
  ariaLabel,
}: ButtonProps) {
  const classes = `inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-colors ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  const content = (
    <>
      {icon && <i className={`${icon} text-base`} aria-hidden="true" />}
      <span>{children}</span>
      {iconRight && <i className={`${iconRight} text-base`} aria-hidden="true" />}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={classes}
        aria-label={ariaLabel}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {content}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} className={classes} aria-label={ariaLabel}>
      {content}
    </button>
  );
}